import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  GuaranteesApprovalStatus,
  SortDirection,
  TaskFetchStatus,
} from '~/modules/tasks/taskModels.ts';
import { getGuaranteesErrorMessage, isGuaranteesApplicationVersionMismatch } from '../taskUtils.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import {
  selectHasTasksUakdoControllerAccess,
  selectHasTasksUakdoResponsibleAccess,
  selectTasksAdministrationRoles,
  selectTasksAdministrationRolesStatus,
  selectTasksAdministrationTasks,
  selectTasksAdministrationTasksError,
  selectTasksAdministrationTasksPageConfiguration,
  selectTasksAdministrationTasksStatus,
  selectTasksPatchAssigneeStatus,
  selectTasksSupervisors,
  selectTasksSupervisorsError,
  selectTasksSupervisorsStatus,
  selectTasksUakdoAccessStatus,
  selectTasksUpdateStatus,
} from '~/store/tasks/selectors.ts';
import {
  fetchTasksAdministrationRoles,
  fetchTasksAdministrationTasks,
  fetchTasksSupervisors,
  fetchTasksUakdoAccess,
  patchTasksAssignee,
  updateTasksApplicationStatus,
  updateTasksUdbtfStatus,
  updateTasksUmvnStatus,
  updateTasksUoStatus,
} from '~/store/tasks/thunks/tasksThunks.ts';
import { selectCurrentUserEmail } from '~/store/users/selectors.ts';
import type { ITasksServiceTableProps } from '../components/types.ts';
import {
  TASKS_MANAGER_TASKS_DATE_SORT_KEY,
  TASKS_ROWS_PER_PAGE,
  tasksPageText,
} from '../constants.ts';
import type { ITaskRow, TTaskRoleKey, TTaskServiceKey } from '../types.ts';
import {
  buildDefaultSelectedTaskRoles,
  buildAvailableGuaranteesTaskRoles,
  getGuaranteesTaskAwaitingStatus,
  getGuaranteesTaskInProgressStatus,
  getVisibleTaskServices,
  isGuaranteesControllerRole,
  mapGuaranteesManagerTasksToTaskRows,
  normalizeSelectedTaskRoles,
} from '../utils.ts';

type TTasksModalAlertState = {
  text: string | ReactNode;
  buttonValue?: string;
  onClick?: () => void;
};

const emptyTasksByService: Record<TTaskServiceKey, ITaskRow[]> = {
  guarantees: [],
};

export const useGuaranteesTasksSection = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const administrationRoles = useSelector(selectTasksAdministrationRoles);
  const administrationRolesStatus = useSelector(selectTasksAdministrationRolesStatus);
  const administrationTasks = useSelector(selectTasksAdministrationTasks);
  const administrationTasksStatus = useSelector(selectTasksAdministrationTasksStatus);
  const administrationTasksError = useSelector(selectTasksAdministrationTasksError);
  const administrationTasksPageConfiguration = useSelector(
    selectTasksAdministrationTasksPageConfiguration
  );
  const taskSupervisors = useSelector(selectTasksSupervisors);
  const taskSupervisorsStatus = useSelector(selectTasksSupervisorsStatus);
  const taskSupervisorsError = useSelector(selectTasksSupervisorsError);
  const patchApplicationAssigneeStatus = useSelector(selectTasksPatchAssigneeStatus);
  const uakdoSupervisorInfoStatus = useSelector(selectTasksUakdoAccessStatus);
  const hasUakdoResponsibleAccess = useSelector(selectHasTasksUakdoResponsibleAccess);
  const hasUakdoControllerAccess = useSelector(selectHasTasksUakdoControllerAccess);
  const updateApplicationStatusStatus = useSelector(selectTasksUpdateStatus);
  const updateUdbtfApprovalStatusStatus = useSelector(selectTasksUpdateStatus);
  const updateUoApprovalStatusStatus = useSelector(selectTasksUpdateStatus);
  const updateUmvnApprovalStatusStatus = useSelector(selectTasksUpdateStatus);
  const currentUserEmail = useSelector(selectCurrentUserEmail);
  const [selectedRole, setSelectedRole] = useState<TTaskRoleKey | ''>('');
  const notifiedSlaDeadlinesRef = useRef(new Set<string>());
  const [guaranteesTasksPage, setGuaranteesTasksPage] = useState(0);
  const [guaranteesDateSortDirection, setGuaranteesDateSortDirection] = useState<SortDirection>(
    SortDirection.Desc
  );
  const [activeGuaranteesActionId, setActiveGuaranteesActionId] = useState<string | null>(null);
  const [assigneeModalRow, setAssigneeModalRow] = useState<ITaskRow | null>(null);
  const [modalAlert, setModalAlert] = useState<TTasksModalAlertState>({ text: '' });
  const guaranteesSort = useMemo(
    () =>
      guaranteesDateSortDirection === SortDirection.None
        ? undefined
        : [`${TASKS_MANAGER_TASKS_DATE_SORT_KEY},${guaranteesDateSortDirection}`],
    [guaranteesDateSortDirection]
  );

  useEffect(() => {
    if (administrationRolesStatus === TaskFetchStatus.Idle) {
      void dispatch(fetchTasksAdministrationRoles());
    }
  }, [administrationRolesStatus, dispatch]);

  useEffect(() => {
    if (uakdoSupervisorInfoStatus === TaskFetchStatus.Idle) {
      void dispatch(fetchTasksUakdoAccess());
    }
  }, [dispatch, uakdoSupervisorInfoStatus]);

  const availableRoles = useMemo<TTaskRoleKey[]>(() => {
    return buildAvailableGuaranteesTaskRoles(administrationRoles, {
      hasUakdoResponsibleAccess,
      hasUakdoControllerAccess,
    });
  }, [administrationRoles, hasUakdoResponsibleAccess, hasUakdoControllerAccess]);
  const selectedAdministrationRoles = useMemo(
    () => (selectedRole ? [selectedRole] : []),
    [selectedRole]
  );
  const shouldFetchAdministrationTasks = selectedAdministrationRoles.length > 0;
  const administrationTasksRequest = useMemo(
    () => ({
      adminRoles: selectedAdministrationRoles,
      page: guaranteesTasksPage,
      size: TASKS_ROWS_PER_PAGE,
      sort: guaranteesSort,
    }),
    [guaranteesSort, guaranteesTasksPage, selectedAdministrationRoles]
  );

  useEffect(() => {
    if (shouldFetchAdministrationTasks) {
      void dispatch(fetchTasksAdministrationTasks(administrationTasksRequest));
    }
  }, [administrationTasksRequest, dispatch, shouldFetchAdministrationTasks]);

  useEffect(() => {
    setSelectedRole((previousRole) => {
      const defaultSelectedRoles = buildDefaultSelectedTaskRoles(availableRoles);
      const [defaultSelectedRole] = defaultSelectedRoles;

      if (!availableRoles.length) {
        return '';
      }

      const normalizedSelectedRoles = normalizeSelectedTaskRoles(
        previousRole ? [previousRole] : [],
        availableRoles
      );
      const [normalizedSelectedRole] = normalizedSelectedRoles;

      return normalizedSelectedRole ?? defaultSelectedRole ?? '';
    });
  }, [availableRoles]);

  const visibleServices = useMemo(
    () => getVisibleTaskServices(selectedAdministrationRoles),
    [selectedAdministrationRoles]
  );
  const hasGuaranteesControllerRole = useMemo(
    () => availableRoles.some(isGuaranteesControllerRole),
    [availableRoles]
  );
  const guaranteesRows = useMemo(
    () =>
      mapGuaranteesManagerTasksToTaskRows(
        shouldFetchAdministrationTasks ? administrationTasks : [],
        { currentUserEmail, selectedRoles: selectedAdministrationRoles }
      ),
    [
      administrationTasks,
      currentUserEmail,
      selectedAdministrationRoles,
      shouldFetchAdministrationTasks,
    ]
  );
  const guaranteesTotalElements = shouldFetchAdministrationTasks
    ? administrationTasksPageConfiguration.totalElements
    : 0;
  const guaranteesPage = shouldFetchAdministrationTasks
    ? administrationTasksPageConfiguration.page
    : 0;
  const isGuaranteesTasksLoading =
    shouldFetchAdministrationTasks && administrationTasksStatus === TaskFetchStatus.Loading;
  const guaranteesTasksError =
    shouldFetchAdministrationTasks && administrationTasksStatus === TaskFetchStatus.Error
      ? administrationTasksError
      : null;
  const tasksByService = useMemo<Record<TTaskServiceKey, ITaskRow[]>>(
    () => ({
      ...emptyTasksByService,
      guarantees: isGuaranteesTasksLoading || guaranteesTasksError ? [] : guaranteesRows,
    }),
    [guaranteesRows, guaranteesTasksError, isGuaranteesTasksLoading]
  );
  const guaranteesEmptyText = useMemo(() => {
    if (isGuaranteesTasksLoading) {
      return tasksPageText.guaranteesLoadingText;
    }

    if (guaranteesTasksError) {
      return guaranteesTasksError.message
        ? `${tasksPageText.guaranteesErrorText} ${guaranteesTasksError.message}`
        : tasksPageText.guaranteesErrorText;
    }

    return undefined;
  }, [guaranteesTasksError, isGuaranteesTasksLoading]);

  const handleSelectRole = (role: TTaskRoleKey) => {
    if (selectedRole === role) {
      return;
    }

    setGuaranteesTasksPage(0);
    setSelectedRole(role);
  };

  const handleGuaranteesDateSortToggle = () => {
    setGuaranteesDateSortDirection((currentDirection) => {
      if (currentDirection === SortDirection.Asc) {
        return SortDirection.Desc;
      }

      if (currentDirection === SortDirection.Desc) {
        return SortDirection.None;
      }

      return SortDirection.Asc;
    });
    setGuaranteesTasksPage(0);
  };

  const reloadGuaranteesTasks = () =>
    shouldFetchAdministrationTasks
      ? dispatch(fetchTasksAdministrationTasks(administrationTasksRequest))
      : Promise.resolve();

  const handleCloseAlertModal = () => {
    setModalAlert({ text: '' });
  };

  const handleRefreshGuaranteesTasks = () => {
    setModalAlert({ text: '' });
    void reloadGuaranteesTasks();
  };

  const handleOpenGuaranteesAssigneeModal = async (row: ITaskRow) => {
    if (!row.assigneeUnit || typeof row.version !== 'number') {
      return;
    }

    setAssigneeModalRow(row);

    try {
      await dispatch(
        fetchTasksSupervisors({
          taskId: row.id,
          unit: row.assigneeUnit,
        })
      ).unwrap();
    } catch {
      // The modal renders the request error from store.
    }
  };

  const handleCloseGuaranteesAssigneeModal = () => {
    if (patchApplicationAssigneeStatus === TaskFetchStatus.Loading) {
      return;
    }

    setAssigneeModalRow(null);
  };

  const handleSubmitGuaranteesAssignee = async (assignedEmployeeEmail: string | null) => {
    if (
      !assigneeModalRow?.assigneeUnit ||
      typeof assigneeModalRow.version !== 'number' ||
      patchApplicationAssigneeStatus === TaskFetchStatus.Loading
    ) {
      return;
    }

    try {
      await dispatch(
        patchTasksAssignee({
          applicationId: assigneeModalRow.id,
          version: assigneeModalRow.version,
          payload: {
            unit: assigneeModalRow.assigneeUnit,
            assignedEmployeeEmail,
          },
        })
      ).unwrap();

      setAssigneeModalRow(null);
      await reloadGuaranteesTasks();
    } catch (error) {
      if (isGuaranteesApplicationVersionMismatch(error)) {
        setAssigneeModalRow(null);
        await reloadGuaranteesTasks();
        setModalAlert({
          text: 'Заявку вже оновив хтось інший. Оновіть, будь ласка, вікно.',
          buttonValue: 'Оновити',
          onClick: handleRefreshGuaranteesTasks,
        });

        return;
      }

      setModalAlert({ text: getGuaranteesErrorMessage(error) });
    }
  };

  const handleTakeGuaranteeTaskInWork = async (row: ITaskRow) => {
    const targetStatus = getGuaranteesTaskInProgressStatus(row.guaranteeStatus);

    if (
      !targetStatus ||
      typeof row.version !== 'number' ||
      row.isAssignedToCurrentUser !== true ||
      activeGuaranteesActionId
    ) {
      return;
    }

    setActiveGuaranteesActionId(row.id);

    try {
      if (row.internalApprovalType === 'udbtf') {
        await dispatch(
          updateTasksUdbtfStatus({
            status: GuaranteesApprovalStatus.IN_PROGRESS,
            applicationId: row.id,
            version: row.version,
          })
        ).unwrap();
      } else if (row.internalApprovalType === 'uo') {
        await dispatch(
          updateTasksUoStatus({
            status: GuaranteesApprovalStatus.IN_PROGRESS,
            applicationId: row.id,
            version: row.version,
          })
        ).unwrap();
      } else if (row.internalApprovalType === 'umvn') {
        await dispatch(
          updateTasksUmvnStatus({
            status: GuaranteesApprovalStatus.IN_PROGRESS,
            applicationId: row.id,
            version: row.version,
          })
        ).unwrap();
      } else {
        await dispatch(
          updateTasksApplicationStatus({
            applicationId: row.id,
            status: targetStatus,
            version: row.version,
          })
        ).unwrap();
      }

      if (row.actionPath) {
        navigate(row.actionPath);
      } else {
        await reloadGuaranteesTasks();
      }
    } catch (error) {
      if (isGuaranteesApplicationVersionMismatch(error)) {
        await reloadGuaranteesTasks();
        setModalAlert({
          text: 'Заявку вже оновив хтось інший. Оновіть, будь ласка, вікно.',
          buttonValue: 'Оновити',
          onClick: handleRefreshGuaranteesTasks,
        });

        return;
      }

      setModalAlert({ text: getGuaranteesErrorMessage(error) });
    } finally {
      setActiveGuaranteesActionId(null);
    }
  };

  const handleRefuseGuaranteeTask = async (row: ITaskRow) => {
    const targetStatus = getGuaranteesTaskAwaitingStatus(row.guaranteeStatus);

    if (
      row.internalApprovalType ||
      !targetStatus ||
      typeof row.version !== 'number' ||
      activeGuaranteesActionId
    ) {
      return;
    }

    setActiveGuaranteesActionId(row.id);

    try {
      await dispatch(
        updateTasksApplicationStatus({
          applicationId: row.id,
          status: targetStatus,
          version: row.version,
        })
      ).unwrap();

      await reloadGuaranteesTasks();
    } finally {
      setActiveGuaranteesActionId(null);
    }
  };

  const guaranteesTableProps = {
    activeGuaranteesActionId,
    canAssignGuaranteesTask: hasGuaranteesControllerRole,
    emptyText: guaranteesEmptyText,
    isLoading:
      isGuaranteesTasksLoading ||
      updateApplicationStatusStatus === TaskFetchStatus.Loading ||
      updateUdbtfApprovalStatusStatus === TaskFetchStatus.Loading ||
      updateUoApprovalStatusStatus === TaskFetchStatus.Loading ||
      updateUmvnApprovalStatusStatus === TaskFetchStatus.Loading,
    page: isGuaranteesTasksLoading || guaranteesTasksError ? 0 : guaranteesPage,
    rowsPerPage: TASKS_ROWS_PER_PAGE,
    guaranteesDateSortDirection,
    totalElements: isGuaranteesTasksLoading || guaranteesTasksError ? 0 : guaranteesTotalElements,
    onGuaranteesDateSortToggle: handleGuaranteesDateSortToggle,
    onGuaranteesAssignTask: handleOpenGuaranteesAssigneeModal,
    onGuaranteesRefuseTask: handleRefuseGuaranteeTask,
    onGuaranteesSlaDeadlineReached: (row: ITaskRow) => {
      const notificationKey = `${row.id}:${row.slaDeadline ?? ''}`;

      if (notifiedSlaDeadlinesRef.current.has(notificationKey)) {
        return;
      }

      notifiedSlaDeadlinesRef.current.add(notificationKey);
      void reloadGuaranteesTasks();
    },
    onGuaranteesTakeInWork: handleTakeGuaranteeTaskInWork,
    onPageChange: setGuaranteesTasksPage,
  } satisfies Partial<ITasksServiceTableProps>;

  const assigneeModalProps = {
    currentEmail: taskSupervisors?.assignedEmployeeEmail ?? assigneeModalRow?.assigneeEmail ?? null,
    errorText:
      taskSupervisorsStatus === TaskFetchStatus.Error
        ? taskSupervisorsError?.message || tasksPageText.assignExecutorErrorText
        : null,
    isLoading: taskSupervisorsStatus === TaskFetchStatus.Loading,
    isOpen: !!assigneeModalRow,
    isSaving: patchApplicationAssigneeStatus === TaskFetchStatus.Loading,
    supervisorEmails: taskSupervisors?.supervisorEmails ?? [],
    onClose: handleCloseGuaranteesAssigneeModal,
    onSubmit: handleSubmitGuaranteesAssignee,
  };

  return {
    assigneeModalProps,
    availableRoles,
    guaranteesTableProps,
    modalAlert,
    onModalAlertClose: handleCloseAlertModal,
    roleFilterHandlers: {
      onSelectRole: handleSelectRole,
    },
    selectedRole,
    tasksByService,
    visibleServices,
  };
};
